import { theme } from '@buggy/shared'
import type { ApexOptions } from 'apexcharts'
import Image from 'next/image'
import { FunctionComponent, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  Card,
  CardTitle,
  KolAvatar,
  KolName,
  PostContent,
  PostImage,
  PostInfo,
  PostList,
  PostListItem,
  PostListItemBorderBottom,
  PostRank,
  PostValue,
  PostValueLabel,
  SlideContainer,
  SlideTitle,
} from '@/utils/export-pptx/components/slides/styles'
import {
  chartSize,
  PptxObjectType,
  SPECIAL_CHAR,
} from '@/utils/export-pptx/constants'
import useApexChartDataUri from '@/utils/export-pptx/hooks/use-apex-chart-data-uri'
import { sliceTextByDisplayLength } from '@/utils/export-pptx/slice-text-by-display-length'
import type { KolPostRankingSlide } from '@/utils/export-pptx/types/slide'
import { getThumbnail } from '@/utils/get-thumbnails'

const MAX_POST_TITLE_LENGTH = 48

interface SlideProps {
  slide: KolPostRankingSlide
  onSlideReady?: (slide: KolPostRankingSlide) => void
  formatNumber: OurIntlShape['formatNumber']
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatNumber,
  formatMessage,
}) => {
  const { data, categories, avg } = useMemo(() => {
    const { data, categories, total } = slide.payload.kolRanking
      .toSorted((x, y) => y.value - x.value)
      .reduce<{
        data: number[]
        categories: string[]
        total: number
      }>(
        (acc, item) => {
          acc.data.push(item.value)
          acc.categories.push(item.name.replaceAll(SPECIAL_CHAR, ''))
          acc.total += item.value

          return acc
        },
        { data: [], categories: [], total: 0 },
      )

    const avg = total / slide.payload.kolRanking.length

    return { data, categories, avg }
  }, [slide.payload.kolRanking])

  const options: ApexOptions = useMemo(() => {
    const annotations = slide.payload.showAverage
      ? {
          yaxis: [
            {
              y: avg,
              strokeDashArray: 0,
              borderColor: theme.colors.text.secondary,
              label: {
                text:
                  `${formatMessage({ id: 'general:project_average' })} ${formatNumber(
                    avg,
                    { minimumFractionDigits: 0, maximumFractionDigits: 2 },
                  )}` + (slide.payload.isPercentage ? '%' : ''),
                borderColor: theme.colors.text.secondary,
                offsetY: 6,
                style: {
                  color: theme.colors.text.white,
                  background: theme.colors.text.secondary,
                  padding: { top: 4, bottom: 4, left: 6, right: 6 },
                },
              },
            },
          ],
        }
      : undefined

    return {
      series: [{ data }],
      chart: { type: 'bar', width: chartSize.bar.w, height: chartSize.bar.h },
      dataLabels: {
        formatter: (value: number): string =>
          formatNumber(value, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }) + (slide.payload.isPercentage ? '%' : ''),
      },
      xaxis: { categories },
      yaxis: {
        labels: { formatter: (value) => String(Math.round(value)) },
        title: {
          text: slide.payload.isPercentage ? '% (Percentage)' : '',
        },
      },
      annotations,
    }
  }, [
    slide.payload.showAverage,
    slide.payload.isPercentage,
    avg,
    formatMessage,
    data,
    categories,
    formatNumber,
  ])

  const dataUri = useApexChartDataUri(options)

  useEffect(() => {
    if (!dataUri) {
      return
    }

    onSlideReady?.(slide)
  }, [onSlideReady, slide, dataUri])

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Shape}>
        <PlatformImage
          height={54}
          platform={slide.payload.platform}
          width={54}
        />
        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({
            id: 'insight_report:slide_kol_performance_analysis',
          })}
        </span>
      </SlideTitle>

      <Card data-pptx-object={PptxObjectType.Shape}>
        <CardTitle data-pptx-object={PptxObjectType.Text}>
          {slide.payload.title}
        </CardTitle>

        <GridContent>
          <ChartContainer>
            {dataUri && (
              <Image
                data-original-size
                alt='kol-interaction-rate-chart'
                data-pptx-object={PptxObjectType.Image}
                height={chartSize.bar.h}
                src={dataUri}
                width={chartSize.bar.w}
              />
            )}
          </ChartContainer>

          <PostListContainer>
            <PostList>
              <PostValueLabel data-pptx-object={PptxObjectType.Text}>
                {slide.payload.label}
              </PostValueLabel>

              {slide.payload.postRanking.slice(0, 3).map((item, index) => (
                <PostListItem key={item.kol.uuid}>
                  <PostRank data-pptx-object={PptxObjectType.Text}>
                    #{index + 1}
                  </PostRank>
                  <PostImage
                    alt={`post-img-${index}`}
                    data-pptx-object={PptxObjectType.Image}
                    src={item.post.thumbnail}
                  />
                  <PostInfo>
                    <PostContent data-pptx-object={PptxObjectType.Text}>
                      {sliceTextByDisplayLength(
                        item.post.title,
                        MAX_POST_TITLE_LENGTH,
                      )}
                    </PostContent>
                    <div>
                      <KolAvatar
                        alt={`kol-avatar-${index}`}
                        data-pptx-object={PptxObjectType.Image}
                        src={getThumbnail(item.kol.uuid)}
                      />

                      <KolName data-pptx-object={PptxObjectType.Text}>
                        {item.kol.name}
                      </KolName>
                    </div>
                  </PostInfo>

                  <PostValue data-pptx-object={PptxObjectType.Text}>
                    {formatNumber(item.value, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}
                    {slide.payload.isPercentage && '%'}
                  </PostValue>

                  {index < 2 && (
                    <PostListItemBorderBottom
                      data-pptx-object={PptxObjectType.Shape}
                    />
                  )}
                </PostListItem>
              ))}
            </PostList>
          </PostListContainer>
        </GridContent>

        {slide.payload.summary && (
          <Summary data-pptx-object={PptxObjectType.Text}>
            {slide.payload.summary}
          </Summary>
        )}
      </Card>
    </SlideContainer>
  )
}

const GridContent = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
`

const ChartContainer = styled.div`
  height: ${chartSize.bar.h}px;
`

const PostListContainer = styled.div`
  min-width: 0;
`

const Summary = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 140%;
  color: ${({ theme }): string => theme.colors.text.primary};
`

export default SlideComponent
